import {
    LanguageCode,
    PrepCheckResultsDTO,
    PrepCheckUserSubmissionDTO,
    PrepCheckDraftDetails,
    PrepCheckResultOverview,
    PrepCheckUserStats,
} from '@hazadapt-git/public-core-base'
import { GuestStorageError, GuestStorageSaveResult } from '../entities'
import { AxiosResponse } from 'axios'
import { isa } from '../api'
import {
    getLocalItem,
    getLocalJSON,
    isLocalStorageAvailable,
    removeLocalItem,
    setLocalJSON,
} from '../browser-storage'
import { getGuestZip } from './guest-user'
import guestJourneyPhaseImage from '../../assets/images/guest-journey-phase.svg'
import { store } from '../store'

const GUEST_PC_SUBMISSIONS_KEY = 'hazadapt_guest_pc_submissions'
const GUEST_PC_RESULTS_KEY = 'hazadapt_guest_pc_results'
const GUEST_PC_IDS_KEY = 'hazadapt_guest_pc_ids'

export const GUEST_PREP_CHECK_LIMIT = 1

type GuestPrepCheckSubmissions = {
    [prepCheckId: number]: PrepCheckUserSubmissionDTO
}

/**
 * Submit and score a guest prep check submission
 * @param submission the prep check submission
 * @returns Promise<PrepCheckResultsDTO> The scored results
 */
export const submitGuestPrepCheck = async (
    submission: PrepCheckUserSubmissionDTO
): Promise<{
    results: PrepCheckResultsDTO
    resultsSaved: boolean
    submissionSaved: GuestStorageSaveResult
}> => {
    const currentLocation = store.getState().profile.currentLocation
    const guestZip = getGuestZip()
    const language = getLocalItem('hazadapt_language') || LanguageCode.ENGLISH

    const response: AxiosResponse<PrepCheckResultsDTO> = await isa.post(
        `/public/guest/prep-check-submissions?lang=${encodeURIComponent(
            language
        )}`,
        {
            ...submission,
            current_zip: currentLocation?.zip ?? guestZip,
            current_county: currentLocation?.county,
            current_state: currentLocation?.state,
        }
    )

    if (response.status !== 201 && response.status !== 200) {
        throw new Error('Unable to submit guest prep check')
    }

    const results = response.data
    const resultsSaved = saveGuestPrepCheckResults(
        submission.prep_check_id,
        results
    )
    const submissionSaved = saveGuestPrepCheckSubmission(submission)

    return { results, resultsSaved, submissionSaved }
}

/**
 * Get all guest prep check submissions from localStorage
 * @returns Object containing all guest submissions keyed by prep check ID
 */

export const getAllGuestSubmissions = (): GuestPrepCheckSubmissions => {
    if (!isLocalStorageAvailable()) return {}
    const parsed = getLocalJSON<GuestPrepCheckSubmissions>(
        GUEST_PC_SUBMISSIONS_KEY,
        {}
    )
    if (
        typeof parsed !== 'object' ||
        parsed === null ||
        Array.isArray(parsed)
    ) {
        console.warn(
            `Invalid data structure for ${GUEST_PC_SUBMISSIONS_KEY}, clearing...`
        )
        removeLocalItem(GUEST_PC_SUBMISSIONS_KEY)
        return {}
    }
    return parsed
}

/**
 * Save a guest prep check submission to localStorage
 * @param submission The prep check submission to save
 * @returns Result object with success status and error details
 */
export const saveGuestPrepCheckSubmission = (
    submission: PrepCheckUserSubmissionDTO
): GuestStorageSaveResult => {
    if (!isLocalStorageAvailable()) {
        return {
            success: false,
            error: GuestStorageError.STORAGE_UNAVAILABLE,
            errorMessage:
                'Browser storage is not available. Your progress cannot be saved.',
        }
    }

    // Validate prep_check_id is a positive number

    if (
        typeof submission.prep_check_id !== 'number' ||
        submission.prep_check_id <= 0
    ) {
        return {
            success: false,
            error: GuestStorageError.INVALID_DATA,
            errorMessage: 'Invalid prep check data.',
        }
    }

    try {
        const submissions = getAllGuestSubmissions()

        // Update the specific prep check submission
        submissions[submission.prep_check_id] = submission
        setLocalJSON(GUEST_PC_SUBMISSIONS_KEY, submissions)

        // Also save the prep check ID to the list of started prep checks
        saveGuestPrepCheckId(submission.prep_check_id)
        return { success: true }
    } catch (error) {
        // Handle QuotaExceededError (localStorage full)
        // Broader QuotaExceededError compatibility (Chrome, Firefox)
        if (
            error instanceof DOMException &&
            (error.name === 'QuotaExceededError' ||
                error.name === 'NS_ERROR_DOM_QUOTA_REACHED')
        ) {
            return {
                success: false,
                error: GuestStorageError.QUOTA_EXCEEDED,
                errorMessage:
                    'Browser storage is full. Please clear some browser data or create a free account to continue.',
            }
        }
        console.error('Failed to save guest prep check submission:', error)
        return {
            success: false,
            error: GuestStorageError.SAVE_FAILED,
            errorMessage: 'Failed to save your progress. Please try again.',
        }
    }
}

/**
 * Get a specific guest prep check submission from localStorage
 * @param prepCheckId The prep check ID to retrieve
 * @returns The guest prep check submission, or null if none exists
 */
export const getGuestPrepCheckSubmission = (
    prepCheckId: number
): PrepCheckUserSubmissionDTO | null => {
    if (!isLocalStorageAvailable()) {
        return null
    }

    try {
        const submissions = getAllGuestSubmissions()
        const submission = submissions[prepCheckId]

        if (!submission) return null
        if (
            typeof submission.prep_check_id !== 'number' ||
            !Array.isArray(submission.submitted_responses) ||
            !submission.status
        ) {
            console.warn(
                'Invalid guest prep check data structure for ID',
                prepCheckId,
                'clearing...'
            )
            delete submissions[prepCheckId]
            setLocalJSON(GUEST_PC_SUBMISSIONS_KEY, submissions)
            return null
        }

        return submission
    } catch (error) {
        console.error('Failed to parse guest prep check submission:', error)
        return null
    }
}

/**
 * Save a guest prep check ID to localStorage
 * @param prepCheckId
 */
export const saveGuestPrepCheckId = (prepCheckId: number): void => {
    if (!isLocalStorageAvailable()) {
        return
    }

    const existingIds = getLocalJSON<number[]>(GUEST_PC_IDS_KEY, [])
    let ids: number[] = Array.isArray(existingIds) ? existingIds : []
    if (!ids.includes(prepCheckId) && ids.length < GUEST_PREP_CHECK_LIMIT) {
        ids.push(prepCheckId)
        setLocalJSON(GUEST_PC_IDS_KEY, ids)
    }
}

/**
 * Get all guest prep check IDs that have been started
 * @returns Array of prep check IDs
 */
export const getGuestStartedPrepCheckIds = (): number[] => {
    const startedPrepCheckIds = getLocalJSON<number[]>(GUEST_PC_IDS_KEY, [])
    if (!Array.isArray(startedPrepCheckIds)) {
        return []
    }
    return startedPrepCheckIds
}

/**
 * Check if guest can access a specific prep check
 * @param prepCheckId The prep check ID to check
 * @returns true if guest can access (has already started it or is under limit), false otherwise
 */
export const canGuestAccessPrepCheck = (prepCheckId: number): boolean => {
    const startedPrepChecks = getGuestStartedPrepCheckIds()

    // Can access if already started
    if (startedPrepChecks.includes(prepCheckId)) {
        return true
    }

    // Can access if under limit
    return startedPrepChecks.length < GUEST_PREP_CHECK_LIMIT
}

/**
 * Remove one guest prep check submission/result from localStorage
 * @param prepCheckId The prep check ID to remove
 * @returns true if removed successfully, false otherwise
 */
export const removeOneGuestPrepCheck = (prepCheckId: number): boolean => {
    try {
        // Remove submission for this prep check
        const submissions = (getLocalJSON(GUEST_PC_SUBMISSIONS_KEY) ||
            {}) as GuestPrepCheckSubmissions
        if (submissions[prepCheckId]) {
            delete submissions[prepCheckId]
            setLocalJSON(GUEST_PC_SUBMISSIONS_KEY, submissions)
        }

        // Remove results for this prep check
        const results = (getLocalJSON(GUEST_PC_RESULTS_KEY) || {}) as {
            [prepCheckId: number]: PrepCheckResultsDTO
        }
        if (results[prepCheckId]) {
            delete results[prepCheckId]
            setLocalJSON(GUEST_PC_RESULTS_KEY, results)
        }
        return true
    } catch (error) {
        console.error('Failed to clear guest prep check data:', error)
        return false
    }
}

/**
 * Save guest prep check results to localStorage
 * @param prepCheckId The prep check ID
 * @param results The results to save
 * @returns true if saved successfully, false otherwise
 */
export const saveGuestPrepCheckResults = (
    prepCheckId: number,
    results: PrepCheckResultsDTO
): boolean => {
    if (!isLocalStorageAvailable()) {
        return false
    }

    try {
        const allResults = getGuestPrepCheckResultsMap()
        allResults[prepCheckId] = results
        setLocalJSON(GUEST_PC_RESULTS_KEY, allResults)
        return true
    } catch (error) {
        return false
    }
}

/**
 * Get all guest prep check results from localStorage (internal helper)
 * @returns Object containing all guest results keyed by prep check ID
 */
const getGuestPrepCheckResultsMap = (): {
    [prepCheckId: number]: PrepCheckResultsDTO
} => {
    if (!isLocalStorageAvailable()) return {}
    const parsed = getLocalJSON<{ [prepCheckId: number]: PrepCheckResultsDTO }>(
        GUEST_PC_RESULTS_KEY,
        {}
    )
    if (
        typeof parsed !== 'object' ||
        parsed === null ||
        Array.isArray(parsed)
    ) {
        console.warn(
            `Invalid data structure for ${GUEST_PC_RESULTS_KEY}, clearing...`
        )
        removeLocalItem(GUEST_PC_RESULTS_KEY)
        return {}
    }
    return parsed
}

/**
 * Get guest prep check results from localStorage
 * @param prepCheckId The prep check ID
 * @returns The results, or null if none exist
 */
export const getGuestPrepCheckResults = (
    prepCheckId: number
): PrepCheckResultsDTO | null => {
    if (!isLocalStorageAvailable()) {
        return null
    }

    try {
        const allResults = getGuestPrepCheckResultsMap()
        return allResults[prepCheckId] || null
    } catch (error) {
        console.error('Failed to get guest prep check results:', error)
        return null
    }
}

/**
 * Get the guest prep check progress as a decimal (0-1)
 * @param prepCheckId The prep check ID to check
 * @returns The progress percentage (0-1)
 */
export const getGuestPrepCheckProgress = (prepCheckId: number): number => {
    try {
        const submission = getGuestPrepCheckSubmission(prepCheckId) as
            | (PrepCheckUserSubmissionDTO & { total_questions?: number })
            | null
        if (!submission?.submitted_responses?.length) return 0

        // total_questions must be set for accurate progress calculation
        // If not set, return 0 since we can't calculate progress without knowing the total
        if (!submission.total_questions) {
            return 0
        }

        const answeredCount = submission.submitted_responses.filter(
            (q) => q.user_answer && q.user_answer.length > 0
        ).length

        return answeredCount / submission.total_questions
    } catch (error) {
        console.error('Failed to get guest prep check progress:', error)
        return 0
    }
}

/**
 * Get guest submission summaries as PrepCheckDraftDetails
 * @returns Array of guest submissions matching auth user format
 */
export const getGuestSubmissionSummaries = (): PrepCheckDraftDetails[] => {
    const submissions = getAllGuestSubmissions()
    return Object.entries(submissions).map(
        ([id, submission]) =>
            ({
                id: Number(id),
                amountDone: getGuestPrepCheckProgress(Number(id)),
            } as PrepCheckDraftDetails)
    )
}

/**
 * Get guest result summaries in a format similar to auth user results
 * Mirrors the structure of PrepCheckResultOverview for consistency
 * @returns Array of guest result summaries with prep_check_id, score, and badge
 */
export const getGuestResultSummaries =
    (): Partial<PrepCheckResultOverview>[] => {
        const results = getGuestPrepCheckResultsMap()
        return Object.entries(results).map(([id, result]) => ({
            prep_check_id: Number(id),
            score: result.score,
            badge: result.badge,
        }))
    }

/**
 * Get default guest stats in a format similar to auth user results
 * Mirrors the structure of PrepCheckUserStats for consistency
 * @returns PrepCheckUserStats object with default values for guests
 */
export const getDefaultGuestStats = (
    copy: 'prep-check-done' | 'prep-check-landing'
): PrepCheckUserStats => {
    const copyMap = {
        'prep-check-done': {
            journeyPhaseSubcopy:
                'Smart move! Your first Prep Check is done! Create a free HazID account to save your results and unlock more.',
        },
        'prep-check-landing': {
            journeyPhaseSubcopy:
                'Start a Prep Check to see how ready you are. Create a free HazID account to save your results and unlock more.',
        },
    }

    return {
        journeyPhase: 'Guest',
        journeyPhaseIcon: {
            src: guestJourneyPhaseImage,
            url: '',
            alt: 'A person holding a map stands at a signpost, curious about where to go next',
        },
        ...copyMap[copy],
        numCompleted: 0,
        numTotal: 0,
    }
}
